import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  BracketConfigEntryDTO,
  CarrierAccountDTO,
  CarrierWeightBracketDTO,
} from '@invenco/shipping-interface';

import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';
import { useEntityDetailsQuery, useGatewayMutation } from '@/shared/hooks/queries';

/**
 * Loads an existing carrier account, its joined carrier, and its weight-bracket configs, plus the
 * mutation to save a config. Scoped to the entity and its direct relations — UI-specific derivations
 * (service names, available services, per-tab splitting) live in the components that need them.
 */
export function useCarrierAccount(carrierAccountId: string) {
  const { shippingGateway } = useGateways();
  const queryClient = useQueryClient();
  const bracketsQueryKey = ['carrierAccounts', carrierAccountId, 'carrierWeightBrackets'];

  // There is no GET-by-id endpoint for a carrier account, so find it in the paginated list.
  const {
    data: carrierAccount,
    isLoading: isLoadingAccount,
    isError: isAccountError,
  } = useEntityDetailsQuery<CarrierAccountDTO>({
    parentKey: 'carrierAccounts',
    id: carrierAccountId,
    query: async (accountId, { signal }) => {
      const accounts = await fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarrierAccounts({ cursor, take }, { signal }),
      );
      const matched = accounts.find((a) => a.id === accountId);
      if (!matched) throw new Error('Carrier account not found');
      return matched;
    },
    retry: false,
  });

  const {
    data: weightBrackets,
    isLoading: isLoadingBrackets,
    isError: isBracketsError,
    refetch: refetchBrackets,
  } = useQuery({
    queryKey: bracketsQueryKey,
    queryFn: ({ signal }) => shippingGateway.getCarrierWeightBrackets(carrierAccountId, { signal }),
  });

  // The carrier is a joined entity (the summary card needs its tracking URL); there's no GET-by-id,
  // so it's found in the list, same as the account.
  const { data: carriers = [] } = useQuery({
    queryKey: ['carriers'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarriers({ cursor, take }, { signal }),
      ),
  });
  const carrier = useMemo(
    () => carriers.find((c) => c.id === carrierAccount?.carrierId),
    [carriers, carrierAccount?.carrierId],
  );

  // A weight-bracket row isn't a backend record — the whole config is one `CarrierWeightBracket` —
  // so a save re-persists the full `bracketsConfig`. A config with no id yet is created; otherwise
  // it's updated. The saved record is written straight into the brackets cache (no refetch), so a
  // freshly-created config immediately carries its id for any follow-up save.
  const { mutate: saveBrackets } = useGatewayMutation<
    CarrierWeightBracketDTO,
    { carrierServiceId: string | null; bracketId?: string; bracketsConfig: BracketConfigEntryDTO[] }
  >({
    mutationFn: ({ carrierServiceId, bracketId, bracketsConfig }) =>
      bracketId
        ? shippingGateway.updateCarrierWeightBracket(carrierAccountId, bracketId, {
            bracketsConfig,
          })
        : shippingGateway.createCarrierWeightBracket(carrierAccountId, {
            carrierServiceId,
            bracketsConfig,
          }),
    successMessage: 'Weight brackets saved',
    onSuccess: (saved) => {
      queryClient.setQueryData<CarrierWeightBracketDTO[]>(bracketsQueryKey, (current) => {
        const list = current ?? [];
        return list.some((bracket) => bracket.id === saved.id)
          ? list.map((bracket) => (bracket.id === saved.id ? saved : bracket))
          : [...list, saved];
      });
    },
  });

  return {
    carrierAccount,
    carrier,
    weightBrackets,
    isLoadingAccount,
    isLoadingBrackets,
    hasError: isAccountError || isBracketsError,
    refetchBrackets,
    saveBrackets,
  };
}

export type CarrierAccountData = ReturnType<typeof useCarrierAccount>;
